import React, { useEffect } from 'react';
import {
  Box, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SectionCard from '../SectionCard';
import InvButton from '../InvButton';
import { InvTextField, InvSelect } from '../InvField';
import InvItemAutocomplete from '../InvItemAutocomplete';
import { emptyWoLine, isPricedWoLine, woLineAmounts, woFinancials, digitsOnly } from '../../utils/woEntity';
import { GST_RATE_OPTIONS } from '../../constants/rfqManagement';
import { formatCurrency } from '../../utils';
import { defaultUom, itemGstPercent, purchaseUomCodeOf, resolveItemGst } from '../../utils/itemDisplay';
import { IPD_COLORS } from '../../theme';

const CELL = { py: 1.25, verticalAlign: 'middle' };

export default function WoLineItemsEditor({
  lines, onChange, showRates = true, disabled = false,
  getPurchaseUomCode, getBaseUomCode, getPurchaseUomId, getGstRate,
}) {
  function addLine(afterIndex) {
    const row = emptyWoLine();
    if (afterIndex === undefined) {
      onChange([...lines, row]);
      return;
    }
    const next = [...lines];
    next.splice(afterIndex + 1, 0, row);
    onChange(next);
  }

  function removeLine(index) {
    if (lines.length <= 1) return;
    onChange(lines.filter((_, i) => i !== index));
  }

  function updateLine(index, patch) {
    onChange(lines.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function onItemPick(index, itemId, item) {
    if (!item && !itemId) {
      updateLine(index, { item_id: '', item_code: '' });
      return;
    }
    const uomId = defaultUom(item, 'purchase') || getPurchaseUomId?.(itemId) || item?.uom_id || '';
    const catalog = getPurchaseUomCode ? {
      getPurchaseUomCode,
      getBaseUomCode,
    } : null;
    updateLine(index, {
      item_id: itemId || '',
      item_name: item?.item_name || item?.item_short_name || lines[index].item_name,
      item_code: item?.item_code || '',
      uom_id: uomId,
      uom_code: purchaseUomCodeOf(item, catalog) || getPurchaseUomCode?.(itemId) || getBaseUomCode?.(itemId) || '',
      gst_percent: itemGstPercent(item) || getGstRate?.(itemId) || '0',
    });
    resolveItemGst(itemId, item).then((gst) => {
      if (!gst) return;
      onChange((prev) => (Array.isArray(prev) ? prev : lines).map((row, i) => (
        i === index ? { ...row, gst_percent: gst } : row
      )));
    });
  }

  useEffect(() => {
    if (!getPurchaseUomCode && !getGstRate) return;
    let changed = false;
    const next = lines.map((line) => {
      if (!line.item_id) return line;
      const code = getPurchaseUomCode?.(line.item_id) || getBaseUomCode?.(line.item_id) || '';
      const gst = getGstRate?.(line.item_id);
      let patch = line;
      const currentGst = String(patch.gst_percent ?? '');
      if (code && line.uom_code !== code) {
        changed = true;
        patch = {
          ...patch,
          uom_code: code,
          uom_id: getPurchaseUomId?.(line.item_id) || line.uom_id,
        };
      }
      if ((currentGst === '' || currentGst === '0') && gst !== '' && gst != null && currentGst !== String(gst)) {
        changed = true;
        patch = { ...patch, gst_percent: gst };
      }
      return patch;
    });
    if (changed) onChange(next);
    // Fill purchase UOM / GST once the item catalog is ready; keyed by selected items.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines.map((line) => line.item_id).join('|'), getPurchaseUomCode, getGstRate]);

  const ready = lines.filter(isPricedWoLine).length;
  const finance = woFinancials(lines);
  const gstOptions = GST_RATE_OPTIONS.map((o) => ({ value: o.value, label: o.label }));

  return (
    <SectionCard
      title="Item / service details"
      subtitle={showRates
        ? 'Catalogue item or a typed custom service. Rates are used on Direct Work Orders.'
        : 'Vendors will fill rates when they quote. You can type a custom service name.'}
      actions={(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
            {ready} line{ready !== 1 ? 's' : ''}
          </Typography>
          {!disabled && (
            <InvButton size="small" startIcon={<AddIcon />} onClick={() => addLine()}>
              Add Row
            </InvButton>
          )}
        </Box>
      )}
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Table
          size="small"
          sx={{
            minWidth: showRates ? 980 : 720,
            '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 36 }}>#</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 200 }}>Item / service *</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 140 }}>Description</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 90 }}>Qty *</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 90 }}>UOM</TableCell>
              {showRates && (
                <>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 90 }}>Rate</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 80 }}>Disc %</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 90 }}>GST %</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 110 }} align="right">Amount</TableCell>
                </>
              )}
              <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 100 }}>Remarks</TableCell>
              <TableCell sx={{ width: 44 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, i) => {
              const amt = showRates ? woLineAmounts(line).total : 0;
              return (
                <TableRow key={line.id || i}>
                  <TableCell sx={CELL}>{i + 1}</TableCell>
                  <TableCell sx={CELL}>
                    <InvTextField
                      value={line.item_name}
                      label=""
                      placeholder="Type item or service name…"
                      disabled={disabled}
                      onChange={(e) => updateLine(i, {
                        item_name: e.target.value,
                        item_id: '',
                      })}
                    />
                    {!disabled && (
                      <Box sx={{ mt: 0.75 }}>
                        <InvItemAutocomplete
                          value={line.item_id}
                          onChange={(id, item) => onItemPick(i, id, item)}
                          placeholder="Optional: pick from catalogue"
                        />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={CELL}>
                    <InvTextField
                      value={line.description}
                      label=""
                      disabled={disabled}
                      onChange={(e) => updateLine(i, { description: e.target.value })}
                    />
                  </TableCell>
                  <TableCell sx={CELL}>
                    <InvTextField
                      value={line.quantity}
                      label=""
                      placeholder="0"
                      inputProps={{ inputMode: 'numeric' }}
                      disabled={disabled}
                      onChange={(e) => updateLine(i, { quantity: digitsOnly(e.target.value) })}
                    />
                  </TableCell>
                  <TableCell sx={{ ...CELL, color: IPD_COLORS.textSecondary, fontSize: 13 }}>
                    {line.uom_code || '—'}
                  </TableCell>
                  {showRates && (
                    <>
                      <TableCell sx={CELL}>
                        <InvTextField
                          value={line.rate}
                          label=""
                          placeholder="0"
                          inputProps={{ inputMode: 'numeric' }}
                          disabled={disabled}
                          onChange={(e) => updateLine(i, { rate: digitsOnly(e.target.value) })}
                        />
                      </TableCell>
                      <TableCell sx={CELL}>
                        <InvTextField
                          value={line.discount_percent}
                          label=""
                          type="number"
                          inputProps={{ min: 0, step: 'any' }}
                          disabled={disabled}
                          onChange={(e) => updateLine(i, { discount_percent: e.target.value })}
                        />
                      </TableCell>
                      <TableCell sx={CELL}>
                        <InvSelect
                          value={String(line.gst_percent ?? '0')}
                          onChange={(e) => updateLine(i, { gst_percent: e.target.value })}
                          options={gstOptions}
                          disabled={disabled}
                        />
                      </TableCell>
                      <TableCell sx={CELL} align="right">
                        {isPricedWoLine(line) && line.rate !== '' ? formatCurrency(amt) : '—'}
                      </TableCell>
                    </>
                  )}
                  <TableCell sx={CELL}>
                    <InvTextField
                      value={line.remarks}
                      label=""
                      disabled={disabled}
                      onChange={(e) => updateLine(i, { remarks: e.target.value })}
                    />
                  </TableCell>
                  <TableCell sx={CELL}>
                    {!disabled && (
                      <IconButton
                        size="small"
                        aria-label="Remove row"
                        disabled={lines.length <= 1}
                        onClick={() => removeLine(i)}
                        sx={{ color: IPD_COLORS.error.text }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      {showRates && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mt: 2, px: 1 }}>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
            Taxable {formatCurrency(finance.subtotal)}
          </Typography>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
            GST {formatCurrency(finance.tax_amount)}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            Total {formatCurrency(finance.grand_total)}
          </Typography>
        </Box>
      )}
      <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 1 }}>
        Search the catalogue or type a name. Catalogue items use the purchase UOM and it cannot be changed.
      </Typography>
    </SectionCard>
  );
}
